import { useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";
import { 
  Mail, Phone, MapPin, Facebook, Instagram, Linkedin, Youtube,
  Shield, Loader2, Send, ChevronRight, FileText, Scale, BookOpen, Monitor, Car, Home, Package,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ALL_COMPANIES } from "@/config/companyEntities";
import { useZonesGroupedByCountry } from "@/hooks/useZones";
import { CountryFlag } from "@/components/CountryFlag";

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t, localizedPath } = useI18n();
  const fl = t.footerLinks;
  const { data: zonesCountries } = useZonesGroupedByCountry();

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { toast.error(t.footer.emailPlaceholder); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) { toast.error(fl.invalidEmail); return; }
    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("subscribe-newsletter", { body: { email: email.trim(), source: "footer" } });
      if (error) throw error;
      toast.success(t.footer.subscribe);
      setEmail("");
    } catch (error) {
      console.error("Newsletter error:", error);
      toast.error(fl.errorRetry);
    } finally { setIsSubmitting(false); }
  };

  const serviciosTradicionales = [
    { name: fl.gestoriaAdmin, path: "/servicios/gestoria-administracion" },
    { name: fl.asesoriaLegal, path: "/servicios/asesoria-legal" },
    { name: fl.formacionIdiomas, path: "/servicios/cursos-idiomas" },
    { name: fl.segurosPensiones, path: "/servicios/consultoria-seguros" },
    { name: "Asesor Fiscal España–Suiza", path: "/servicios/asesoria-fiscal-espana-suiza" },
  ];
  const subserviciosGestoria = [
    { name: fl.desempleoRav, path: "/servicios/gestoria-administracion/desempleo" },
    { name: fl.administracion, path: "/servicios/gestoria-administracion/administracion" },
    { name: fl.cvDoc, path: "/servicios/gestoria-administracion/cv" },
    { name: fl.acompanamientos, path: "/servicios/gestoria-administracion/acompanamientos" },
    { name: fl.traducciones, path: "/servicios/gestoria-administracion/traducciones" },
    { name: fl.paquetes, path: "/servicios/gestoria-administracion/paquetes" },
  ];
  const serviciosLifestyle = [
    { name: fl.vehiculos, path: "/servicios/alquileres-vehiculos" },
    { name: fl.viviendas, path: "/servicios/alquileres-viviendas" },
    
    { name: fl.solucionesDigitales, path: "/servicios/soluciones-digitales-avanzadas" },
  ];
  const empresa = [
    { name: fl.sobreNosotros, path: "/sobre-nosotros" },
    { name: fl.blog, path: "/blog" },
    { name: fl.contacto, path: "/contacto" },
  ];
  const legal = [
    { name: fl.privacidad, path: "/politicas/privacidad" },
    { name: fl.avisoLegal, path: "/politicas/aviso-legal" },
    { name: fl.cookies, path: "/politicas/cookies" },
    { name: fl.gdpr, path: "/politicas/gdpr" },
  ];

  const socialLinks = [
    { name: "Facebook", icon: Facebook, url: "https://www.facebook.com/p/Punto-Hispano-61553177270040/" },
    { name: "Instagram", icon: Instagram, url: "https://www.instagram.com/puntohispanoch/" },
    { name: "TikTok", icon: TikTokIcon, url: "https://www.tiktok.com/@puntohispano.ch" },
    { name: "LinkedIn", icon: Linkedin, url: "https://linkedin.com/company/puntohispano" },
    { name: "YouTube", icon: Youtube, url: "https://youtu.be/Wt-P_6MDTao?si=CEsyc0CW_VGchShy" },
  ];

  const FooterLinkSection = ({ title, icon: Icon, links }: { title: string; icon?: React.ElementType; links: { name: string; path: string }[] }) => (
    <div>
      <h2 className="flex items-center gap-2 mb-5">
        <span className="w-1.5 h-1.5 rounded-full bg-punto shrink-0" />
        {Icon && <Icon className="w-3.5 h-3.5 shrink-0 text-punto/70" />}
        <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-white/90">{title}</span>
      </h2>
      <ul className="space-y-2.5">
        {links.map((item) => (
          <li key={item.path}>
            <Link to={localizedPath(item.path)} className="group flex items-center gap-2 text-[13px] text-white/50 hover:text-white transition-all duration-300">
              <ChevronRight className="w-3 h-3 shrink-0 text-punto/0 group-hover:text-punto group-hover:translate-x-0.5 -ml-1 transition-all duration-300" />
              {item.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <footer className="relative w-full bg-[#050505] text-white overflow-hidden">
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-punto/60 to-transparent" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-punto/[0.03] rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-14 pb-28 sm:pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 pb-12 border-b border-white/[0.06]">
          <div>
            <Link to={localizedPath("/")} className="inline-flex items-center gap-3 mb-5 group">
              <img src="/punto-hispano-logo.ico" alt="Punto Hispano" className="h-10 w-auto object-contain group-hover:drop-shadow-[0_0_8px_rgba(168,28,28,0.4)] transition-all duration-300" />
              <span className="text-lg font-bold text-white tracking-wide">PUNTO<span className="text-punto">HISPANO</span></span>
            </Link>
            <p className="text-white/45 text-sm leading-relaxed mb-6 max-w-md">{t.footer.brandDesc}</p>
            <div className="flex flex-col gap-2.5">
              {[
                { href: "https://maps.google.com/?q=Thurgauerstrasse+117,+8152+Opfikon", icon: MapPin, text: "Thurgauerstrasse 117, 8152 Opfikon, Suiza", external: true },
                { href: "tel:+41797700342", icon: Phone, text: "+41 79 770 03 42" },
                { href: "mailto:info@puntohispano.ch", icon: Mail, text: "info@puntohispano.ch" },
              ].map((item) => (
                <a key={item.text} href={item.href} {...(item.external ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="group flex items-center gap-3 px-3.5 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06] hover:border-punto/30 hover:bg-white/[0.05] transition-all duration-300">
                  <item.icon className="w-3.5 h-3.5 shrink-0 text-punto/70 group-hover:text-punto transition-colors" />
                  <span className="text-white/60 text-xs group-hover:text-white/90 transition-colors">{item.text}</span>
                </a>
              ))}
            </div>
          </div>
          <div className="flex flex-col justify-between">
            <div className="p-6 rounded-xl bg-white/[0.02] border border-white/[0.06]">
              <h2 className="text-[11px] font-semibold tracking-[0.2em] uppercase mb-2 text-white/90">{t.footer.newsletter}</h2>
              <p className="text-white/70 text-xs mb-4 leading-relaxed">{t.footer.newsletterDesc}</p>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/60 pointer-events-none" />
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.footer.emailPlaceholder} disabled={isSubmitting} className="w-full pl-9 pr-3 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-white text-xs placeholder:text-white/60 focus:outline-none focus:border-punto/50 focus:shadow-[0_0_12px_rgba(168,28,28,0.15)] transition-all duration-300 disabled:opacity-50" />
                </div>
                <button type="submit" disabled={isSubmitting} className="px-5 py-2.5 bg-punto/90 hover:bg-punto text-white font-semibold text-xs rounded-lg flex items-center gap-1.5 hover:shadow-[0_0_20px_rgba(168,28,28,0.3)] transition-all duration-300 disabled:opacity-50 whitespace-nowrap">
                  {isSubmitting ? <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin" /> : <Send className="w-3.5 h-3.5 shrink-0" />}
                  {t.footer.subscribe}
                </button>
              </form>
            </div>
            <div className="flex flex-wrap gap-2.5 mt-6">
              {socialLinks.map((social) => (
                <a key={social.name} href={social.url} target="_blank" rel="noopener noreferrer" aria-label={`${fl.followUs} ${social.name}`} className="w-10 h-10 rounded-full bg-white/[0.04] border border-white/[0.08] flex items-center justify-center text-white/50 hover:text-white hover:border-punto/40 hover:bg-punto/10 hover:shadow-[0_0_12px_rgba(168,28,28,0.2)] transition-all duration-300">
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 py-12 border-b border-white/[0.06]">
          <FooterLinkSection title={fl.servicios} icon={FileText} links={serviciosTradicionales} />
          <FooterLinkSection title={fl.gestoria} icon={Scale} links={subserviciosGestoria} />
          <FooterLinkSection title={fl.lifestyle} icon={Package} links={serviciosLifestyle} />
          <FooterLinkSection title={fl.empresa} icon={BookOpen} links={empresa} />
          <FooterLinkSection title={fl.legal} icon={Shield} links={legal} />
        </div>




        <div className="pt-8 pb-6 border-b border-white/[0.06]">
          <h2 className="text-[11px] font-semibold tracking-[0.2em] uppercase mb-4 text-white/85">Entidades del Grupo</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {ALL_COMPANIES.map((c) => (
              <div key={c.uid} className="px-3.5 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                <p className="text-[11px] font-medium text-white/80 leading-tight">{c.name}</p>
                <p className="text-[10px] text-white/75 mt-1 font-mono tracking-wide">{c.uid}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[11px] text-white/80">© {currentYear} Punto Hispano Holding GmbH. {t.footer.rights}.</p>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-[10px] font-medium text-white/50">🇨🇭 Swiss Made</span>
            <a href="https://puntohispano.ch" className="text-[11px] text-white/75 hover:text-punto transition-colors duration-300">puntohispano.ch</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
